"use client";

import { useEffect, useRef, useState } from "react";
import { AlertTriangle, ChevronDown, Search } from "lucide-react";
import { cn } from "../../lib/utils";
import type { MultiSelectDropdownProps } from "./multi-select-dropdown.types";

export function MultiSelectDropdown({
  singleSelect = false,
  triggerClassName,
  disabled = false,
  ariaLabel,
  side = "bottom",
  options,
  selected,
  onToggle,
  placeholder,
  searchable = false,
  searchPlaceholder,
  emptyMessage,
  selectedCountLabel,
  noResultsMessage,
  noJobsLabel,
  onSearchChange,
}: MultiSelectDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchQuery("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen, searchable]);

  const filteredOptions =
    searchable && searchQuery
      ? options.filter(
          (opt) =>
            opt.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
            opt.value.toLowerCase().includes(searchQuery.toLowerCase()),
        )
      : options;

  const displayText =
    selected.length === 0
      ? placeholder
      : singleSelect
        ? (options.find((option) => option.value === selected[0])?.label ??
          placeholder)
        : selectedCountLabel.replace("{count}", String(selected.length));

  // Check if any selected items are unavailable
  const hasUnavailableSelected = selected.some(
    (v) => options.find((o) => o.value === v)?.isUnavailable,
  );

  return (
    <div
      className="relative"
      ref={dropdownRef}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          setIsOpen(false);
          dropdownRef.current?.querySelector("button")?.focus();
        }
      }}
    >
      <button
        type="button"
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        disabled={disabled}
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "w-full flex items-center justify-between rounded-lg border bg-white px-3 py-2.5 text-sm text-[#101828]",
          hasUnavailableSelected ? "border-amber-400" : "border-black",
          triggerClassName,
        )}
      >
        <span
          className={cn(
            selected.length === 0 ? "text-[#667085]" : "",
            hasUnavailableSelected && "flex items-center gap-1.5",
          )}
        >
          {hasUnavailableSelected && (
            <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
          )}
          {displayText}
        </span>
        <ChevronDown
          className={cn("h-4 w-4 transition-transform", isOpen && "rotate-180")}
        />
      </button>

      {isOpen && !disabled && (
        <div
          className={cn(
            "absolute z-50 w-full rounded-lg border border-[#E4E7EC] bg-white shadow-lg overflow-hidden",
            side === "top" ? "bottom-full mb-1" : "mt-1",
          )}
        >
          {searchable && (
            <div className="p-2 border-b border-[#E4E7EC]">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#667085]" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    onSearchChange?.(e.target.value);
                  }}
                  placeholder={searchPlaceholder}
                  className="w-full pl-8 pr-3 py-2 text-sm border border-[#E4E7EC] rounded-md focus:outline-none focus:ring-1 focus:ring-[#0D41E1]"
                />
              </div>
            </div>
          )}
          <div className="max-h-48 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-4 text-sm text-[#667085] text-center">
                {searchQuery ? noResultsMessage : emptyMessage}
              </div>
            ) : (
              filteredOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={selected.includes(option.value)}
                  onClick={() => {
                    onToggle(option.value);
                    if (singleSelect) {
                      setIsOpen(false);
                      dropdownRef.current?.querySelector("button")?.focus();
                    }
                  }}
                  className={cn(
                    "w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-[#F9FAFB]",
                    selected.includes(option.value) &&
                      !option.isUnavailable &&
                      "bg-[#EBF5FF]",
                    selected.includes(option.value) &&
                      option.isUnavailable &&
                      "bg-amber-50",
                    option.isUnavailable && "opacity-75",
                  )}
                >
                  <span className="flex items-center gap-2">
                    {option.isUnavailable && (
                      <AlertTriangle className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
                    )}
                    <span
                      className={option.isUnavailable ? "text-amber-700" : ""}
                    >
                      {option.label}
                    </span>
                  </span>
                  <span className="text-xs text-[#667085]">
                    {option.isUnavailable
                      ? noJobsLabel
                      : option.count?.toLocaleString()}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
